import { BestPriceStrategy, BestPriceStrategyItem } from 'api'
import { take } from 'lodash'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { BestPriceType } from 'store/grid/gridTypes'
import { isNotNil } from 'utils/predicate'
import { bestPriceTypeSelector } from './gridSelectors'
import { bestPriceStoreSelector } from './storeSelectors'

export const bestPriceByEntityIdsSelector: Selector<GlobalState, Record<string, BestPriceStrategyItem>> = createSelector(
  bestPriceTypeSelector,
  bestPriceStoreSelector,
  (bestPriceType, bestPriceStore) => {
    const bestPrices = Object.values(bestPriceStore)
      .map(e => {
        if (bestPriceType === BestPriceType.REPO_INTEREST) {
          const repoInterestRankedItems = e.lastUpdate?.entity?.strategies?.REPO_INTEREST?.rankedItems || []
          return repoInterestRankedItems.length ? repoInterestRankedItems[0] : null
        } else {
          const weightedAverageRankedItems = e.lastUpdate?.entity?.strategies?.WEIGHTED_AVERAGE_RATE?.rankedItems || []
          return weightedAverageRankedItems.length ? weightedAverageRankedItems[0] : null
        }
      })
      .filter(isNotNil)

    return bestPrices.reduce<Record<string, BestPriceStrategyItem>>((dict, bestPrice) => {
      dict[bestPrice.entityId] = bestPrice

      return dict
    }, {})
  }
)

export const bestPriceByNegotiationIdGetterSelector: Selector<
  GlobalState,
  (negotiationId: string) => BestPriceStrategyItem | undefined
> = createSelector(bestPriceByEntityIdsSelector, bestPriceByNegotiationId => (negotiationId: string) => {
  return bestPriceByNegotiationId?.[negotiationId]
})

export const bestPricesByParentIdGetterSelector: Selector<
  GlobalState,
  /** parentId can be either the parentOrderId or the collectionId */
  (parentId: string, count: number) => BestPriceStrategyItem[]
> = createSelector(bestPriceTypeSelector, bestPriceStoreSelector, (bestPriceType, bestPriceStore) => (parentId: string, count: number) => {
  const strategyType =
    bestPriceType === BestPriceType.REPO_INTEREST ? BestPriceStrategy.REPO_INTEREST : BestPriceStrategy.WEIGHTED_AVERAGE_RATE

  const bestPrices = bestPriceStore[parentId]?.lastUpdate?.entity?.strategies?.[strategyType]?.rankedItems ?? []

  return take(bestPrices, count)
})

export const isBestPriceEligibleNegotiation: Selector<GlobalState, (rfqId: string) => boolean> = createSelector(
  bestPriceStoreSelector,
  bestPriceStore => (parentOrderId: string) => {
    const bestPriceEligible = Object.values(bestPriceStore).find(bestPrice => bestPrice.entityId === parentOrderId)
    return bestPriceEligible ? true : false
  }
)
